import { Button, Checkbox, ConfigProvider, Dropdown, Flex, Form, Input, InputRef, Space, Table, TableColumnType, theme, Typography, DatePicker } from "antd";
import zhCN from "antd/lib/locale/zh_CN";
import { ColumnConfigItemType  } from "../../../../utils/datatype";
import { SearchOutlined, UndoOutlined, UnorderedListOutlined, CalendarOutlined } from '@ant-design/icons';
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { WarningDataParams } from "../../utils/data.type";
import { getPlatformList, getWarningData, getWarningTypeList } from "../../service";
import { FieldData } from "rc-field-form/lib/interface";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const { useToken } = theme;
const { Text } = Typography;

const columnConfig : Array<ColumnConfigItemType> = [
    {
        dataIndex: 'id',
        title: 'ID',
        className: 'td-column',
        isShow: true,
        width: 80,
        canSearch: true,
        sorter: {
            compare: (a: any, b: any) => a.id - b.id
        }
    },
    {
        dataIndex: 'labelUrl',
        title: '渠道名称',
        className: 'td-column',
        isShow: true,
        width: 140,
        canSearch: true,
    },
    {
        dataIndex: 'countryCode',
        title: '国家代码',
        className: 'td-column',
        isShow: true, 
        width: 120,
        canSearch: true,
    },
    {
        dataIndex: 'media',
        title: '平台',
        className: 'td-column',
        isShow: true,
        width: 80,
        onFilter: (value, record) => record.media.indexOf(value) === 0
    },
    // {
    //     dataIndex: 'isWarningSent',
    //     title: '是否被警告',
    //     className: 'td-column',
    //     isShow: true,
    //     width: 150,
    //     canSearch: true,
    // },
    {
        dataIndex: 'warningType',
        title: '警告类型',
        className: 'td-column',
        isShow: true,
        width: 100,
        onFilter: (value, record) => record.warningType.indexOf(value) === 0
    },
    {
        dataIndex: 'remark',
        title: '警告详情',
        className: 'td-column',
        isShow: true,
        width: 180,
        render: (text: string) => {
            return <span>{text}</span>;
        },
        canSearch: true
    },
    {
        dataIndex: 'roi',
        title: 'ROI',
        className: 'td-column',
        isShow: true,
        width: 80,
        sorter: {
            compare: (a: any, b: any) => a.roi - b.roi
        }
    },
    {
        dataIndex: 'cost',
        title: '成本',
        className: 'td-column',
        isShow: true,
        width: 80,
        sorter: {
            compare: (a: any, b: any) => a.cost - b.cost
        }
    },
    {
        dataIndex: 'roiPercent',
        title: 'ROI百分比',
        className: 'td-column',
        isShow: true,
        width: 120,
    },
    {
        dataIndex: 'changePercentage',
        title: '涨跌幅',
        className: 'td-column',
        isShow: true,
        width: 100,
    },
    // {
    //     dataIndex: 'warningSentTime',
    //     title: '警告时间',
    //     className: 'td-column',
    //     isShow: true,
    //     width: 140,
    // },
    {
        dataIndex: 'queryDate',
        title: '警告日期',
        className: 'td-column',
        isShow: true,
        width: 100,
        render: (value) => {
            return <span>{value}</span>
        },
        canSearch: true,
        searchType: 'time'
    },
    {
        dataIndex: 'queryHour',
        title: '警告时刻',
        className: 'td-column',
        isShow: true,
        width: 100,
        render: (value) => {
            return <span>{value}</span>
        },
        sorter: {
            compare: (a: any, b: any) => a.queryHour - b.queryHour
        }
    }
];



export default function WarningPage() {

    const [ searchParams, setSearchParams ] = useState<WarningDataParams>({});
    // 动态设置表格宽度
    const [tableWidth, setTableWidth] = useState(columnConfig.reduce((prev, current: ColumnConfigItemType) => prev + (current.width as number), 0));
    // 设置筛选数据
    const [filterData, setFilterData] = useState<any>({});
    // 刷新数据
    const [refresh, setRefresh] = useState<boolean>();
    // 列表字段集合 - 用来控制列表列数据的展示与隐藏
    const [columnFields, setColumnFields] = useState<string[]>(columnConfig.map(item => item.dataIndex));
    // 列搜索INput Ref
    const searchInput = React.createRef<InputRef>();
    // token
    const { token } = useToken();



    const {data} = useQuery({
        queryKey: ['warning', searchParams, refresh],
        queryFn: () => getWarningData(searchParams),
        select: (data) => {
            data = data.map(item => {
                return {
                    ...item,
                    key: item.id
                }
            })
            return data;
        }
    });

    const onColumnChange = (changedFields: FieldData[], allFields: FieldData[]) => {
        const fields = (allFields.filter((item) => item.value)).map(item => item.name[0]);
        setColumnFields(fields);
    }

    const handleSearch = (
        selectedKeys: React.Key[],
        confirm: () => void, 
        dataIndex: string
    ) => {
        confirm();
    }

    const handleTime = (
        selectedKeys: string[],
        confirm: () => void,
        setSelectedKeys: (value: string[]) => void,
        defaultValue: string[]
    ) => {
        if(selectedKeys.length === 0){
            setSelectedKeys(defaultValue);
        }
        confirm();
    }

    const handleReset = (
        clearFilters: () => void,
        confirm: () => void
    ) => {
        clearFilters();
        confirm();
    }

    const refreshData = () => {
        setRefresh(!refresh);
    }
    /**
     * @function getColumnSearchProps - 设置列的搜索属性: 当type为time时, // !下拉框为时间选择器
     * @param dataIndex 
     * @returns 
     */
    const getColumnSearchProps = (dataIndex: any, type?: 'time'): TableColumnType<any> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            type === 'time' ? 
            <Space style={{ padding: 8 }} direction='vertical'>
                <RangePicker 
                    maxDate={dayjs()} 
                    className="budget-filter" 
                    defaultValue={[dayjs(), dayjs()]}
                    format={'YYYYMMDD'}
                    onChange={ (date, dateString) => setSelectedKeys(dateString) }
                />
                <Space>
                    <Button 
                        type="primary" 
                        onClick={() => handleTime(selectedKeys as string[], confirm, setSelectedKeys, [dayjs().format('YYYYMMDD'), dayjs().format('YYYYMMDD')])}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, confirm)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </Space>
            : 
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                ref={searchInput}
                placeholder={`Search ${dataIndex}`}
                value={selectedKeys[0]}
                onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters, confirm)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => {
            return type === 'time' ?
            <CalendarOutlined style={{ color: filtered ? 'red' : undefined }} />
            :
            <SearchOutlined style={{ color: filtered ? 'red' : undefined }} />
        },
        onFilter: (value, record) =>
            record[dataIndex]
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        }
    });

    useEffect(() => {
        getPlatformList().then(res => {
            setFilterData(Object.assign({}, { media: res}, filterData));
        });

        getWarningTypeList().then(res => {
            setFilterData(Object.assign({}, { warningType:  res }, filterData));
        });
        return () => {
        }
    }, []);


    const columnConfigFiltered = useMemo(() => {
        const tableWidth = columnConfig.reduce((prev, current: ColumnConfigItemType) => {
            return prev + (columnFields.includes(current.dataIndex) ? current.width as number : 0);
        }, 0); 
        setTableWidth(tableWidth);
        const tempColumns = columnConfig.map(col => 
            {
                // 设置列的search属性
                if(col.canSearch){
                    col = col.searchType === 'time' ? Object.assign(col, getColumnSearchProps(col.dataIndex, 'time')) : Object.assign(col, getColumnSearchProps(col.dataIndex));
                }

                // 设置列的filter属性
                if(col.onFilter && filterData[col.dataIndex]){
                    col.filters = filterData[col.dataIndex].map((item: any) => {
                        return {
                            text: item,
                            value: item
                        }
                    });
                }

                // 设置列的render方法
                if(!col.render){
                    col.render = (value, record) => {
                        return <span>{value}</span>
                    }
                }
                // 设置列的hidden属性
                if(columnFields.includes(col.dataIndex)){
                    col.hidden = false;
                }else{
                    col.hidden = true;
                }
                return col;
            }
        )
        return tempColumns;
    }, [filterData , columnFields]);



    return <ConfigProvider locale={zhCN} theme={{
        token: {
            fontSize: 16,
            paddingSM: 16,
            controlHeight: 40
        },
        components: {
            Table: {
                headerBg: '#F2F5F9',
                headerColor: '#52c41a',
                headerSortHoverBg: '#F0FAFF',
                headerSortActiveBg: '#95de64'
            },
            Tabs: {
                titleFontSizeLG: 28
            }
        }
    }}>
        <Flex vertical={true} gap={16}>
            <Flex vertical={true} gap={16} align="strench">
                <Space>
                    <Button onClick={refreshData} icon={<UndoOutlined />} shape="circle"></Button>
                    <Dropdown overlayStyle={{marginRight: '0'}} trigger={['click']} dropdownRender={(menus) => (
                        <ConfigProvider theme={{
                            token: {
                                    controlHeight: 28
                                }
                        }}>
                            <Form style={{background: token.colorBgElevated, borderRadius: token.borderRadiusSM, boxShadow: token.boxShadowSecondary, padding: token.paddingContentVertical}} layout="vertical" onFieldsChange={onColumnChange}>
                                {
                                    columnConfig.map((item, key) => {
                                        const switchKey = item.dataIndex as string;
                                        return  <Form.Item style={{ marginBottom: 0, height: 28 }} initialValue={item.isShow} key={switchKey ?? item.key} valuePropName="checked" name={switchKey ?? item.key}>
                                            {/* <Switch></Switch> */}
                                            <Checkbox>
                                                {item.title as string}
                                            </Checkbox>
                                        </Form.Item>
                                    })
                                }
                            </Form> 
                            </ConfigProvider>
                        ) }>
                        <Button icon={<UnorderedListOutlined />} shape="circle"></Button>
                    </Dropdown>
                </Space>
            </Flex>
            <Table size="middle" rowHoverable={false} bordered={true} columns={columnConfigFiltered} sticky={{ offsetHeader: 0, offsetScroll: 0 }} scroll={{ x: tableWidth }} dataSource={data} pagination={{ defaultPageSize: 500,  pageSizeOptions: [100, 200, 500, 1000], position: ["bottomRight"] }}>
            </Table>
        </Flex>
    </ConfigProvider>
}